import { alpha } from "@mui/material/styles";
import { ALPHA, FONT_SIZE, INK, NEUTRAL, SURFACE } from "@/theme/tokens";
import type { DayToggleCell, DayToggleSize } from "./DayToggleGrid";

/** How an unselected day rests — see `DayToggleGrid`'s `look`. */
export type DayToggleLook = "muted" | "outlined";

/** A chip's resolved tone: the fill of a selected chip, its hover step, and its ink. */
export type DayToggleToneColors = { main: string; dark: string; contrastText: string };

/** The chip's box — shared by the button and the read-only glyph so both modes draw the same shape. */
export const chipGeometrySx = (cellSize: number, stretch: boolean, shape: "square" | "circle") =>
	({
		position: "relative",
		// `auto` under `stretch`: the grid column owns the width, so a cell can be wide enough for a word.
		// Height stays on the size scale either way.
		width: stretch ? "auto" : cellSize,
		height: cellSize,
		flexShrink: 0,
		p: 0,
		// Rounded square by default: a 7-up row of squares reads as one week block, where circles read as
		// seven separate beads. `circle` is the caller's explicit choice.
		borderRadius: shape === "circle" ? "50%" : 1,
	}) as const;

/**
 * The glyph's type; a notch smaller at `xsmall`, so an initial still has air inside a 20px chip. `outlined`
 * reads at body size and weight, as the form's other inputs do.
 */
export const chipTypeSx = (size: DayToggleSize, look: DayToggleLook) =>
	({
		font: "inherit",
		fontSize: look === "outlined" ? FONT_SIZE.SECONDARY : size === "xsmall" ? FONT_SIZE.LABEL_MICRO : FONT_SIZE.LABEL_XS,
		fontWeight: look === "outlined" ? 500 : 700,
		lineHeight: 1,
	}) as const;

/**
 * A look's selected fill and ink, and its resting edge, fill and ink. `outlined` marks a selected day with a soft
 * tint instead of a solid fill, so a whole week switched on reads as a choice, not as a solid block of brand colour.
 */
export const chipLookColors = (colors: DayToggleToneColors, look: DayToggleLook) =>
	look === "outlined"
		? {
				selectedBg: alpha(colors.main, ALPHA.CONTROL_TINT_HOVER),
				selectedInk: colors.dark,
				restBorder: alpha(INK.SECONDARY, ALPHA.FIELD_OUTLINE),
				restBg: SURFACE,
				restInk: INK.SECONDARY,
			}
		: { selectedBg: colors.main, selectedInk: colors.contrastText, restBorder: NEUTRAL[200], restBg: NEUTRAL[50], restInk: INK.DISABLED };

/**
 * A chip's resting fill, edge and ink, shared by both modes so they can't drift: the tone's fill when selected,
 * otherwise the cell's own status `tone` (unselected only — see `DayToggleCell.tone`) or the look's resting chip.
 */
export const chipColorSx = (cell: DayToggleCell, colors: DayToggleToneColors, look: DayToggleLook) => {
	const lookColors = chipLookColors(colors, look);
	return {
		border: `1px solid ${cell.selected ? colors.main : (cell.tone?.border ?? lookColors.restBorder)}`,
		backgroundColor: cell.selected ? lookColors.selectedBg : (cell.tone?.bg ?? lookColors.restBg),
		color: cell.selected ? lookColors.selectedInk : (cell.tone?.ink ?? lookColors.restInk),
	} as const;
};
